import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuGroup,
} from '@/components/ui/dropdown-menu';
import { visibleSections } from '@/lib/navConfig';
import { NavBadge } from './NavBadge';

export const UserMenu = ({ user, badges, onAction, onLogout }) => {
  const { t } = useTranslation();
  const sections = visibleSections(user);
  const pending = badges?.requests || 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 relative" data-testid="user-menu-trigger">
          <User className="h-4 w-4" />
          <span className="hidden sm:inline text-sm">{user.name}</span>
          {pending > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none" data-testid="user-menu-pending-badge">
              {pending > 99 ? '99+' : pending}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 max-h-[80vh] overflow-y-auto" data-testid="user-dropdown-menu">
        {sections.map((section, idx) => {
          const SIcon = section.icon;
          return (
            <div key={section.key}>
              {idx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 ${section.color}`}>
                <SIcon className="h-3 w-3" /> {t(section.labelKey)}
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const count = item.badge ? badges?.[item.badge] || 0 : 0;
                  const testId = item.testId || `nav-${item.key}`;
                  if (item.action) {
                    return (
                      <DropdownMenuItem key={item.key} onClick={() => onAction(item.action)} className="cursor-pointer gap-2" data-testid={testId}>
                        <Icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                        {t(item.labelKey)}
                      </DropdownMenuItem>
                    );
                  }
                  return (
                    <DropdownMenuItem key={item.key} asChild>
                      <Link to={item.to} className="flex items-center justify-between gap-2 w-full" data-testid={testId}>
                        <span className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                          {t(item.labelKey)}
                        </span>
                        <NavBadge count={count} tone={item.badge === 'messages' ? 'blue' : 'red'} testId={`${testId}-badge`} />
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            </div>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} className="text-red-600 cursor-pointer" data-testid="logout-button">
          <LogOut className="h-4 w-4 mr-2" />
          {t('nav.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
